package com.xorrus.trading.lot.relayclient.browser

import kotlinx.browser.window

/**
 * Navigation zwischen connect-SPA (`/`) und den Strategie-SPA-Varianten
 * (`/stable/`, `/latest/`, `/dev/`) auf remote.lab-of-trades.com.
 *
 * Der gewählte Desktop reist als Query-Parameter `d=<Desktop-Kennung>` mit
 * ([ControllerService.Desktop.id]); fehlt er (alter connect-Build, Lesezeichen), gilt der
 * aktuell ausgewählte Desktop ([ControllerService.selected]).
 *
 * Immer `location.replace()`: die jeweils verlassene SPA taucht nicht in der
 * Browser-History auf — Back landet dort, wo der Nutzer VOR der SPA war, statt
 * in einer Redirect-Schleife.
 */
object RemoteNav {

    /** Update-Channels der Desktop-App = deployte Strategie-SPA-Varianten (CI: .publish-lot). */
    val KNOWN_CHANNELS: Set<String> = setOf("stable", "latest", "dev")

    /** Rücksprung-Gründe (`/?reason=…`) — die connect-SPA erklärt damit die Warteseite bzw. Liste. */
    const val REASON_UNPAIRED = "unpaired"
    const val REASON_DISCONNECTED = "disconnected"
    const val REASON_CHANNEL_CHANGED = "channel-changed"

    /** Strategie-SPA bekam `?d=`, kennt diese Kopplung aber nicht (lokal entfernt). */
    const val REASON_UNKNOWN_DESKTOP = "unknown-desktop"

    /** Nutzer will zur Desktop-Liste („Desktop wechseln") — kein Banner, keine Auto-Weiterleitung. */
    const val REASON_SWITCH = "switch"

    const val PARAM_DESKTOP = "d"

    /**
     * Channel-Segment der aktuellen URL (`/stable/…` → `stable`); null, wenn die SPA
     * nicht unter einem bekannten Channel-Prefix läuft (z. B. lokaler Dev-Server) —
     * dann entfallen Channel-Vergleiche.
     */
    fun ownChannel(): String? =
        window.location.pathname.trim('/').substringBefore('/').takeIf { it in KNOWN_CHANNELS }

    /** `reason`-Parameter der aktuellen URL (für die connect-SPA). */
    fun reason(): String? = queryParam("reason")

    /** Desktop-Kennung aus `?d=…`; null = nicht angegeben. */
    fun desktopParam(): String? = queryParam(PARAM_DESKTOP)?.takeIf { it.isNotBlank() }

    private fun queryParam(name: String): String? =
        js("new URLSearchParams(window.location.search)").get(name) as String?

    /** Weiter zur Strategie-SPA des Channels für diesen Desktop — nur für bekannte Channels aufrufen. */
    fun toStrategySpa(channel: String, desktopId: String) {
        window.location.replace("/$channel/?$PARAM_DESKTOP=${encode(desktopId)}")
    }

    /** Zurück zur connect-SPA, optional mit Grund und betroffenem Desktop für die dortige Anzeige. */
    fun toConnectSpa(reason: String? = null, desktopId: String? = null) {
        val params = listOfNotNull(
            reason?.let { "reason=${encode(it)}" },
            desktopId?.let { "$PARAM_DESKTOP=${encode(it)}" },
        )
        window.location.replace(if (params.isEmpty()) "/" else "/?" + params.joinToString("&"))
    }

    private fun encode(value: String): String = js("encodeURIComponent")(value) as String
}
